/**
 * Addon Pricing & Duration Calculation Utilities
 * Ensures transparent and consistent pricing for add-ons considering both quantity, duration, and schedule constraints.
 */

export const DAYS_OF_WEEK = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];

export const DAY_SHORT_LABELS = {
  SUNDAY: "Sun",
  MONDAY: "Mon",
  TUESDAY: "Tue",
  WEDNESDAY: "Wed",
  THURSDAY: "Thu",
  FRIDAY: "Fri",
  SATURDAY: "Sat",
};

export const DAY_FULL_LABELS = {
  SUNDAY: "Sunday",
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
};

/**
 * Get readable single-unit label for addon duration
 * @param {Object} addon
 * @returns {string} e.g. "hour", "day", "week", "month", or "unit"
 */
export const getAddonDurationUnit = addon => {
  const duration = (addon?.duration || addon?.durationType || "").toUpperCase();
  switch (duration) {
    case "HOURLY":
      return "hour";
    case "DAILY":
      return "day";
    case "WEEKLY":
      return "week";
    case "MONTHLY":
      return "month";
    default:
      return "unit";
  }
};

/**
 * Get formatted price display for an add-on
 * @param {Object} addon
 * @param {string} currency
 * @returns {string} e.g. "KES 2,500 / week"
 */
export const getAddonRateDisplay = (addon, currency = "KES") => {
  const price = addon?.unitPrice ?? addon?.price ?? 0;
  const unit = getAddonDurationUnit(addon);
  return `${currency} ${price.toLocaleString()} / ${unit}`;
};

/**
 * Extract available day names from an addon's availableSlots
 * @param {Object} addon
 * @returns {string[] | null} e.g. ["TUESDAY", "THURSDAY"] or null if unconstrained
 */
export const getAddonAvailableDays = addon => {
  const slots = addon?.availableSlots || addon?.availabilitySlots;
  if (!Array.isArray(slots) || slots.length === 0) {
    return null;
  }
  const days = slots
    .map(s => (typeof s.day === "string" ? s.day.toUpperCase() : ""))
    .filter(Boolean);
  const uniqueDays = Array.from(new Set(days));
  return uniqueDays.length > 0 ? uniqueDays : null;
};

/**
 * Format available days for display
 * @param {Object} addon
 * @returns {string} e.g. "Tue, Thu" or "Mon – Fri"
 */
export const formatAddonDaysSummary = addon => {
  const days = getAddonAvailableDays(addon);
  if (!days || days.length === 0) return "";
  if (days.length === 7) return "Every day";

  const isMonFri =
    days.length === 5 &&
    ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"].every(d => days.includes(d));
  if (isMonFri) return "Mon – Fri";

  const isWeekend =
    days.length === 2 && days.includes("SATURDAY") && days.includes("SUNDAY");
  if (isWeekend) return "Weekends (Sat & Sun)";

  const dayOrder = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
  const sorted = dayOrder.filter(d => days.includes(d));
  return sorted.map(d => DAY_SHORT_LABELS[d] || d).join(", ");
};

/**
 * Check if an addon is available on a specific date
 * @param {Object} addon
 * @param {Date|string} date
 * @returns {boolean}
 */
export const isAddonAvailableOnDate = (addon, date) => {
  const days = getAddonAvailableDays(addon);
  if (!days || days.length === 0) return true;
  if (!date) return true;

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return true;
  const dayName = DAYS_OF_WEEK[d.getDay()];
  return days.includes(dayName);
};

/**
 * Get the next upcoming dates that match an addon's schedule (and optionally space schedule)
 * @param {Object} addon
 * @param {Date|string} fromDate
 * @param {Array} spaceAvailableSlots
 * @param {number} count
 * @returns {Array<{ date: Date, formatted: string, dayLabel: string, fullDateString: string }>}
 */
export const getAddonNextAvailableDates = (
  addon,
  fromDate = new Date(),
  spaceAvailableSlots = null,
  count = 3
) => {
  const addonDays = getAddonAvailableDays(addon);
  if (!addonDays || addonDays.length === 0) return [];

  let validDays = addonDays;
  if (Array.isArray(spaceAvailableSlots) && spaceAvailableSlots.length > 0) {
    const spaceDays = spaceAvailableSlots
      .map(s => (typeof s.day === "string" ? s.day.toUpperCase() : ""))
      .filter(Boolean);
    const intersected = addonDays.filter(d => spaceDays.includes(d));
    if (intersected.length > 0) {
      validDays = intersected;
    }
  }

  const start = new Date(fromDate || new Date());
  start.setHours(0, 0, 0, 0);

  const results = [];
  for (let i = 0; i <= 30 && results.length < count; i++) {
    const target = new Date(start);
    target.setDate(start.getDate() + i);
    const dayName = DAYS_OF_WEEK[target.getDay()];

    if (validDays.includes(dayName)) {
      results.push({
        date: target,
        formatted: target.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
        dayLabel: DAY_FULL_LABELS[dayName] || dayName,
        fullDateString: target.toISOString().split("T")[0],
      });
    }
  }

  return results;
};

/**
 * Format a HH:mm or HH:mm:ss string into a readable 12h/24h time label (e.g. 9:00 AM)
 * @param {string} timeStr
 * @returns {string}
 */
export const formatSlotTimeString = timeStr => {
  if (!timeStr || typeof timeStr !== "string") return "";
  const parts = timeStr.trim().split(":");
  if (parts.length < 2) return timeStr;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return timeStr;
  const ampm = hours >= 12 ? "PM" : "AM";
  const h12 = hours % 12 || 12;
  const minStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return `${h12}:${minStr} ${ampm}`;
};

/**
 * Get structured schedule openings (days + hours) for an add-on
 * @param {Object} addon
 * @returns {{ hasSchedule: boolean, openings: Array<{ daysSummary: string, timeSummary: string, formatted: string }>, fullSummary: string }}
 */
export const getAddonScheduleOpenings = addon => {
  const slots = addon?.availableSlots || addon?.availabilitySlots;
  if (!Array.isArray(slots) || slots.length === 0) {
    return {
      hasSchedule: false,
      openings: [],
      fullSummary: "Available anytime with space booking",
    };
  }

  // Group slots by time range (e.g. 09:00-17:00)
  const groupedByTime = new Map();
  slots.forEach(slot => {
    if (!slot) return;
    const day = typeof slot.day === "string" ? slot.day.toUpperCase() : "";
    if (!day) return;
    const start = slot.startTime || "";
    const end = slot.endTime || "";
    const timeKey = start && end ? `${start}|${end}` : "ALL_DAY";

    if (!groupedByTime.has(timeKey)) {
      groupedByTime.set(timeKey, {
        days: [],
        startTime: start,
        endTime: end,
      });
    }
    const group = groupedByTime.get(timeKey);
    if (!group.days.includes(day)) {
      group.days.push(day);
    }
  });

  const dayOrder = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
  const openings = [];

  groupedByTime.forEach((group, timeKey) => {
    const sortedDays = dayOrder.filter(d => group.days.includes(d));
    let daysSummary = "";
    if (sortedDays.length === 7) {
      daysSummary = "Every day";
    } else if (
      sortedDays.length === 5 &&
      ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"].every(d => sortedDays.includes(d))
    ) {
      daysSummary = "Mon – Fri";
    } else if (
      sortedDays.length === 2 &&
      sortedDays.includes("SATURDAY") &&
      sortedDays.includes("SUNDAY")
    ) {
      daysSummary = "Weekends";
    } else {
      daysSummary = sortedDays.map(d => DAY_SHORT_LABELS[d] || d).join(", ");
    }

    let timeSummary = "All day";
    if (group.startTime && group.endTime) {
      const formattedStart = formatSlotTimeString(group.startTime);
      const formattedEnd = formatSlotTimeString(group.endTime);
      timeSummary = `${formattedStart} – ${formattedEnd}`;
    }

    openings.push({
      daysSummary,
      timeSummary,
      formatted: `${daysSummary} • ${timeSummary}`,
      days: sortedDays,
      startTime: group.startTime,
      endTime: group.endTime,
    });
  });

  const fullSummary = openings.map(o => o.formatted).join("; ") || "Available with space";

  return {
    hasSchedule: true,
    openings,
    fullSummary,
  };
};

/**
 * Check compatibility of an addon against the current booking date/details (both day and time hours)
 * @param {Object} addon
 * @param {Date|string} selectedDate
 * @param {Object} bookingDetails
 * @param {Array} spaceAvailableSlots
 * @returns {Object}
 */
export const checkAddonDateCompatibility = (
  addon,
  selectedDate = null,
  bookingDetails = null,
  spaceAvailableSlots = null
) => {
  const targetDateRaw = selectedDate || bookingDetails?.startTime;
  const targetDate = targetDateRaw ? new Date(targetDateRaw) : null;
  const availableDays = getAddonAvailableDays(addon);
  const scheduleOpenings = getAddonScheduleOpenings(addon);
  const hasConstraint = Boolean(availableDays && availableDays.length > 0);

  if (!hasConstraint) {
    return {
      isCompatible: true,
      isDayCompatible: true,
      isTimeCompatible: true,
      hasConstraint: false,
      availableDays: [],
      availableDaysSummary: "",
      scheduleOpenings,
      selectedDayName: "",
      selectedDayLabel: "",
      nextAvailableDates: [],
      warningMessage: null,
    };
  }

  const summary = formatAddonDaysSummary(addon);
  let isDayCompatible = true;
  let isTimeCompatible = true;
  let selectedDayName = "";
  let selectedDayLabel = "";
  let timeWarningDetails = null;

  if (targetDate && !Number.isNaN(targetDate.getTime())) {
    selectedDayName = DAYS_OF_WEEK[targetDate.getDay()];
    selectedDayLabel = targetDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
    isDayCompatible = availableDays.includes(selectedDayName);

    // Check time hours if booking has start and end time
    if (isDayCompatible && bookingDetails?.startTime && bookingDetails?.endTime) {
      const bStart = new Date(bookingDetails.startTime);
      const bEnd = new Date(bookingDetails.endTime);
      const slots = addon?.availableSlots || addon?.availabilitySlots;

      if (Array.isArray(slots) && slots.length > 0) {
        const daySlots = slots.filter(
          s => typeof s.day === "string" && s.day.toUpperCase() === selectedDayName
        );

        const hasTimeRestrictedSlots = daySlots.some(s => s.startTime && s.endTime);
        if (hasTimeRestrictedSlots) {
          const bStartMin = bStart.getHours() * 60 + bStart.getMinutes();
          const bEndMin = bEnd.getHours() * 60 + bEnd.getMinutes();

          const matchesAnySlot = daySlots.some(s => {
            if (!s.startTime || !s.endTime) return true;
            const [sH, sM] = s.startTime.split(":").map(n => parseInt(n, 10));
            const [eH, eM] = s.endTime.split(":").map(n => parseInt(n, 10));
            const slotStartMin = sH * 60 + (sM || 0);
            const slotEndMin = eH * 60 + (eM || 0);
            return bStartMin >= slotStartMin && bEndMin <= slotEndMin;
          });

          if (!matchesAnySlot) {
            isTimeCompatible = false;
            const allowedTimes = daySlots
              .filter(s => s.startTime && s.endTime)
              .map(s => `${formatSlotTimeString(s.startTime)} – ${formatSlotTimeString(s.endTime)}`)
              .join(", ");
            timeWarningDetails = {
              allowedTimes,
              bookingTime: `${bStart.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} – ${bEnd.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`,
            };
          }
        }
      }
    }
  }

  const isCompatible = isDayCompatible && isTimeCompatible;

  const nextAvailableDates = getAddonNextAvailableDates(
    addon,
    targetDate || new Date(),
    spaceAvailableSlots,
    3
  );

  let warningMessage = null;
  if (!isDayCompatible) {
    warningMessage = `"${addon.name || "Add-on"}" is only available on ${summary}. Your selected date is ${selectedDayLabel || "not an available day"}.`;
  } else if (!isTimeCompatible && timeWarningDetails) {
    warningMessage = `"${addon.name || "Add-on"}" hours on ${selectedDayLabel} are ${timeWarningDetails.allowedTimes}. Your booking time (${timeWarningDetails.bookingTime}) falls outside available hours.`;
  }

  return {
    isCompatible,
    isDayCompatible,
    isTimeCompatible,
    hasConstraint,
    availableDays,
    availableDaysSummary: summary,
    scheduleOpenings,
    selectedDayName,
    selectedDayLabel,
    nextAvailableDates,
    warningMessage,
  };
};

/**
 * Calculate the duration multiplier for an addon given a booking timeframe
 * @param {Object} addon
 * @param {Object} bookingDetails - { startTime, endTime }
 * @param {string} spaceDurationType - Space duration type (HOURLY, DAILY, WEEKLY, MONTHLY)
 * @returns {{ units: number, unitLabel: string }}
 */
export const calculateAddonDurationMultiplier = (addon, bookingDetails, spaceDurationType = "HOURLY") => {
  const duration = (addon?.duration || addon?.durationType || "").toUpperCase();

  // If addon is fixed / per-unit (no time duration), multiplier is always 1
  if (!duration || duration === "FIXED" || duration === "PER_UNIT" || duration === "ONE_TIME") {
    return { units: 1, unitLabel: "unit" };
  }

  if (!bookingDetails?.startTime || !bookingDetails?.endTime) {
    return { units: 1, unitLabel: getAddonDurationUnit(addon) };
  }

  const start = new Date(bookingDetails.startTime);
  const end = new Date(bookingDetails.endTime);
  const durationMs = end.getTime() - start.getTime();

  if (Number.isNaN(durationMs) || durationMs <= 0) {
    return { units: 1, unitLabel: getAddonDurationUnit(addon) };
  }

  switch (duration) {
    case "HOURLY": {
      const hours = Math.max(1, Math.ceil(durationMs / (1000 * 60 * 60)));
      return { units: hours, unitLabel: hours === 1 ? "hour" : "hours" };
    }
    case "DAILY": {
      const days = Math.max(1, Math.ceil(durationMs / (1000 * 60 * 60 * 24)));
      return { units: days, unitLabel: days === 1 ? "day" : "days" };
    }
    case "WEEKLY": {
      const days = durationMs / (1000 * 60 * 60 * 24);
      const weeks = Math.max(1, Math.ceil(days / 7));
      return { units: weeks, unitLabel: weeks === 1 ? "week" : "weeks" };
    }
    case "MONTHLY": {
      const days = durationMs / (1000 * 60 * 60 * 24);
      const months = Math.max(1, Math.ceil(days / 30));
      return { units: months, unitLabel: months === 1 ? "month" : "months" };
    }
    default:
      return { units: 1, unitLabel: "unit" };
  }
};

/**
 * Get the default, min, and max duration units for an addon
 * @param {Object} addon
 * @param {Object} bookingDetails - { startTime, endTime }
 * @param {string} spaceDurationType - Space duration type
 * @returns {Object} { defaultUnits, maxUnits, minUnits, unitLabel, isAdjustable }
 */
export const getAddonDefaultUnits = (addon, bookingDetails, spaceDurationType = "HOURLY") => {
  const duration = (addon?.duration || addon?.durationType || "").toUpperCase();
  
  if (!duration || duration === "FIXED" || duration === "PER_UNIT" || duration === "ONE_TIME") {
    return { defaultUnits: 1, maxUnits: 1, minUnits: 1, unitLabel: "unit", isAdjustable: false };
  }

  const multiplier = calculateAddonDurationMultiplier(addon, bookingDetails, spaceDurationType);
  const defaultUnits = multiplier.units;
  
  return { 
    defaultUnits, 
    maxUnits: defaultUnits, 
    minUnits: 1, 
    unitLabel: multiplier.unitLabel, 
    isAdjustable: defaultUnits > 1 
  };
};

/**
 * Calculate the comprehensive price and breakdown for a selected addon
 * @param {Object} addon - Addon object (from space or selectedAddons)
 * @param {number} quantity - Selected quantity
 * @param {Object} bookingDetails - { startTime, endTime }
 * @param {string} spaceDurationType - Space duration type
 * @param {string} currency - Currency code
 * @param {number|null} customUnits - Optional custom duration units
 * @returns {Object} itemized breakdown
 */
export const calculateAddonItemPrice = (
  addon,
  quantity = 1,
  bookingDetails = null,
  spaceDurationType = "HOURLY",
  currency = "KES",
  customUnits = null
) => {
  const unitPrice = addon?.unitPrice ?? addon?.price ?? addon?.ratePrice ?? 0;
  const multiplier = calculateAddonDurationMultiplier(addon, bookingDetails, spaceDurationType);
  const rateUnit = getAddonDurationUnit(addon);
  
  const defaultUnits = multiplier.units || 1;
  const finalUnits = (customUnits !== null && customUnits > 0) ? customUnits : defaultUnits;
  const qty = typeof quantity === "number" && quantity > 0 ? quantity : 1;
  
  const totalPrice = unitPrice * qty * finalUnits;

  return {
    addonId: addon?.addonId ?? addon?.id,
    name: addon?.name || "Add-on",
    unitPrice,
    quantity: qty,
    duration: addon?.duration || addon?.durationType || "FIXED",
    durationUnits: finalUnits,
    durationUnitLabel: multiplier.unitLabel,
    rateUnit,
    totalPrice,
    currency,
    maxUnits: defaultUnits,
    isAdjustable: defaultUnits > 1,
    customUnits: customUnits !== null && customUnits > 0 ? customUnits : null
  };
};

/**
 * Check whether an addon duration is compatible with a space duration
 * e.g., A DAILY addon cannot be attached to an HOURLY space because a 3h booking cannot consume a minimum 1-day block.
 * @param {string} addonDuration - e.g. "HOURLY", "DAILY", "WEEKLY", "MONTHLY", "FIXED"
 * @param {string} spaceDurationType - e.g. "HOURLY", "DAILY", "WEEKLY", "MONTHLY"
 * @returns {{ isCompatible: boolean, reason?: string }}
 */
export const isAddonDurationCompatible = (addonDuration, spaceDurationType = "HOURLY") => {
  const aDur = (addonDuration || "FIXED").toUpperCase();
  const sDur = (spaceDurationType || "HOURLY").toUpperCase();

  // Fixed/One-time addons are compatible with any space duration
  if (aDur === "FIXED" || aDur === "ONE_TIME" || aDur === "NONE" || aDur === "UNIT") {
    return { isCompatible: true };
  }

  const durationRank = {
    HOURLY: 1,
    DAILY: 2,
    WEEKLY: 3,
    MONTHLY: 4,
  };

  const aRank = durationRank[aDur] || 1;
  const sRank = durationRank[sDur] || 1;

  // Addon duration cannot be broader than the space duration
  // e.g. Daily addon (2) > Hourly space (1) -> incompatible
  if (aRank > sRank) {
    return {
      isCompatible: false,
      reason: `${aDur} add-on cannot be attached to a ${sDur} space.`,
    };
  }

  return { isCompatible: true };
};

const addonPricingUtils = {
  DAYS_OF_WEEK,
  DAY_SHORT_LABELS,
  DAY_FULL_LABELS,
  getAddonDurationUnit,
  getAddonRateDisplay,
  getAddonAvailableDays,
  formatAddonDaysSummary,
  isAddonAvailableOnDate,
  getAddonNextAvailableDates,
  checkAddonDateCompatibility,
  calculateAddonDurationMultiplier,
  calculateAddonItemPrice,
  getAddonDefaultUnits,
  isAddonDurationCompatible,
};

export default addonPricingUtils;
